import type { ReactNode } from "react";

import { cn } from "@pontimed/ui";

/** The account h1: Geologica display font, tight tracking. Shared so pages
 *  that can't use <PageHeader> (custom layouts) still match it. */
export const PAGE_TITLE_CLASS =
  "font-display text-foreground text-2xl font-semibold tracking-[-0.02em] text-balance";

interface PageHeaderProps {
  title: ReactNode;
  /** One muted line under the title. Always visible (tabs pattern §4). */
  description?: ReactNode;
  /** Right-aligned chrome (primary CTA, links). Wraps under the title on
   *  narrow screens instead of squeezing it. */
  actions?: ReactNode;
  /** Small element rendered before the title (icon tile, avatar). */
  leading?: ReactNode;
  className?: string;
}

/**
 * Standard page header for portal screens: h1 + optional description +
 * optional actions. No bottom margin — the next block sets the spacing
 * (TabBar `mt-6`, content `mt-6`/`space-y-*`), per the tabs pattern.
 */
export function PageHeader({ title, description, actions, leading, className }: PageHeaderProps) {
  return (
    <div className={cn("flex flex-wrap items-start justify-between gap-3", className)}>
      <div className="flex min-w-0 items-start gap-3">
        {leading}
        <div className="min-w-0">
          <h1 className={PAGE_TITLE_CLASS}>{title}</h1>
          {description ? <p className="text-muted-foreground mt-1 text-sm">{description}</p> : null}
        </div>
      </div>
      {/* Collapses when the slot renders an empty element (e.g. a portal
          target), so it never adds a stray wrapped row on narrow screens. */}
      {actions ? (
        <div className="flex flex-wrap items-center gap-2 [&:has(>:only-child:empty)]:hidden">
          {actions}
        </div>
      ) : null}
    </div>
  );
}
